import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { opsApi } from "@/features/operations/api";

export default function AdminSettlements() {
    const qc = useQueryClient();
    const [form, setForm] = useState({ rider_id: "", amount: 0, note: "" });

    const { data, isLoading } = useQuery({
        queryKey: ["admin", "settlements"],
        queryFn: () => opsApi.listSettlements(),
    });

    const riders = useQuery({
        queryKey: ["admin", "team-riders"],
        queryFn: () => opsApi.listTeam({ role: "rider" }),
    });

    const createMut = useMutation({
        mutationFn: (p: typeof form) => opsApi.createSettlement(p),
        onSuccess: () => {
            toast.success("Cash settled");
            qc.invalidateQueries({ queryKey: ["admin", "settlements"] });
            setForm({ rider_id: "", amount: 0, note: "" });
        },
        onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Failed"),
    });

    const total = data?.items.reduce((s, x) => s + x.amount, 0) ?? 0;

    return (
        <div data-testid="admin-settlements-page" className="space-y-6">
            <header>
                <h1 className="text-2xl font-extrabold">Cash settlements</h1>
                <p className="text-sm text-muted-foreground">
                    Log when a rider hands over COD collections to the store.
                </p>
            </header>

            <div className="grid gap-6 md:grid-cols-3">
                <div className="rounded-2xl border bg-white p-5 md:col-span-1">
                    <div className="flex items-center gap-2 pb-2 text-sm font-bold">
                        <Wallet className="h-4 w-4" /> Record settlement
                    </div>
                    <form
                        className="space-y-3"
                        onSubmit={(e) => {
                            e.preventDefault();
                            createMut.mutate(form);
                        }}
                    >
                        <div>
                            <Label htmlFor="rider">Rider</Label>
                            <select
                                id="rider"
                                data-testid="settlement-rider"
                                className="mt-1 w-full rounded-md border border-border bg-white p-2 text-sm"
                                value={form.rider_id}
                                onChange={(e) => setForm({ ...form, rider_id: e.target.value })}
                                required
                            >
                                <option value="">Select rider…</option>
                                {riders.data?.items.map((r) => (
                                    <option key={r.id} value={r.id}>{r.full_name} ({r.email})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label htmlFor="amt">Amount (₹)</Label>
                            <Input id="amt" data-testid="settlement-amount" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} required />
                        </div>
                        <div>
                            <Label htmlFor="note">Note</Label>
                            <Input id="note" data-testid="settlement-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                        </div>
                        <Button type="submit" className="bg-brand text-white" data-testid="settlement-submit" disabled={createMut.isPending}>
                            Record settlement
                        </Button>
                    </form>
                </div>

                <div className="md:col-span-2">
                    <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                            Total settled: <span className="font-bold text-foreground">₹{total.toLocaleString("en-IN")}</span>
                        </p>
                    </div>
                    <div className="overflow-x-auto rounded-2xl bg-white">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-black/5 text-xs uppercase tracking-widest text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-3">Date</th>
                                    <th className="px-4 py-3">Rider</th>
                                    <th className="px-4 py-3">Amount</th>
                                    <th className="px-4 py-3">Note</th>
                                    <th className="px-4 py-3">By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td className="px-4 py-6 text-center" colSpan={5}>Loading…</td></tr>
                                ) : data?.items.length ? (
                                    data.items.map((s) => {
                                        const r = riders.data?.items.find((x) => x.id === s.rider_id);
                                        return (
                                            <tr key={s.id} className="border-t border-border/40" data-testid={`settlement-row-${s.id}`}>
                                                <td className="px-4 py-3 text-xs">{new Date(s.at).toLocaleString()}</td>
                                                <td className="px-4 py-3">{r?.full_name ?? s.rider_id.slice(-6)}</td>
                                                <td className="px-4 py-3 font-bold">₹{s.amount.toLocaleString("en-IN")}</td>
                                                <td className="px-4 py-3 text-muted-foreground">{s.note || "—"}</td>
                                                <td className="px-4 py-3 text-xs text-muted-foreground">{s.settled_by_email}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr><td className="px-4 py-6 text-center text-muted-foreground" colSpan={5}>No settlements yet</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
